import { useState } from 'react';
import { Button } from './Button.js';
import { Icon } from './Icon.js';

/**
 * O botão de copiar, que **nunca afirma que copiou antes de copiar**.
 *
 * ## Por que ele mora aqui e não na tela que o usou primeiro
 *
 * Nasceu no portal, na segunda via do boleto. Chegou a interna precisando dele
 * para o link de acompanhamento, e o que dois módulos precisam não é de nenhum
 * dos dois — copiado, o segundo teria perdido a disciplina abaixo no primeiro
 * ajuste, e um "Copiado!" que mente é pior do que não ter botão.
 *
 * ## As três coisas que ele faz, e por quê
 *
 * `navigator.clipboard` não existe em contexto inseguro e pode ser recusado pelo
 * navegador mesmo existindo. Então:
 *
 * 1. **não desenha nada** quando a API não existe — um botão que nunca funciona
 *    é pior que a ausência dele;
 * 2. **confirma depois do `await`**, nunca antes. O que se perde ao confiar num
 *    otimista é o pagamento, ou o link que a pessoa achou que mandou;
 * 3. **a recusa diz o que fazer** em vez de sumir.
 *
 * O anúncio é `aria-live`: quem usa leitor de tela não vê o texto aparecer ao
 * lado do botão que acabou de apertar.
 */
export function CopyButton({
  text,
  label,
  /**
   * O que dizer quando não der para copiar.
   *
   * Tem padrão porque a frase certa depende de onde o valor está NA TELA — "a
   * linha acima" só faz sentido quando existe uma linha acima. Quem chama de um
   * lugar onde o valor não está visível passa a sua.
   */
  fallback = 'Não foi possível copiar por aqui. Selecione o texto e copie à mão.',
}: {
  text: string;
  label: string;
  fallback?: string;
}) {
  const [copy, setCopy] = useState<'feita' | 'recusada' | null>(null);
  const canCopiar = typeof navigator !== 'undefined' && Boolean(navigator.clipboard);

  if (!canCopiar) return null;

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="contorno"
        size="sm"
        onClick={() => {
          void navigator.clipboard
            .writeText(text)
            .then(() => setCopy('feita'))
            .catch(() => setCopy('recusada'));
        }}
      >
        <Icon nome="copiar" size={16} />
        {label}
      </Button>
      <p aria-live="polite" className="text-xs text-texto-suave">
        {copy === 'feita' && 'Copiado.'}
        {copy === 'recusada' && fallback}
      </p>
    </div>
  );
}
